import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'

import Box from '@components/kit/box'
import Card from '@components/kit/card'
import { Column, Row } from '@components/kit/grid'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'

import { ChatContext } from '../chat.web'
import { useHealthTrackingStore } from '@store/HealthTracking'
import { IHealthTrackingSteps } from '@store/HealthTracking/types'
import BarChart from '@components/barChart'
import Pressable from '@components/kit/pressable'
import { ChevronDownIcon } from '@components/kit/icons'

const PAGE_SIZE = 30
const RETRACTED_PAGE_SIZE = 10
const BAR_CHART_VALUES_SIZE = 30

function StepsLog() {
  const { userId } = useContext(ChatContext)
  const { getStepsLog } = useHealthTrackingStore.getState()
  const [stepsLog, setStepsLog] = useState<IHealthTrackingSteps | null>(null)
  const [stepsValues, setStepsValues] = useState<number[]>([])
  const [shouldRenderChart, setShouldRenderChart] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [currentPageSize, setCurrentPageSize] =
    useState<number>(RETRACTED_PAGE_SIZE)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const isRetractable = stepsLog?.results.length > RETRACTED_PAGE_SIZE

  const toggleExpanded = () => {
    if (isExpanded) {
      setCurrentPageSize(RETRACTED_PAGE_SIZE)
    } else {
      setCurrentPageSize(PAGE_SIZE)
    }
    setIsExpanded(!isExpanded)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      const data = await getStepsLog(userId, currentPage, PAGE_SIZE)
      // steps values should have the exact size of BAR_CHART_VALUES_SIZE, so we need crop it and fill it with 0 if necessary
      const values =
        data?.results
          .map(steps => steps.value)
          .slice(0, BAR_CHART_VALUES_SIZE) || []
      const emptyValues = Array.from(
        { length: Math.max(BAR_CHART_VALUES_SIZE - values.length, 0) },
        () => 0
      )
      setShouldRenderChart(values.length > 0)
      setStepsValues([...values, ...emptyValues])
      setStepsLog(data)
      setIsLoading(false)
    }
    fetchData()
  }, [userId, currentPage])

  return (
    <React.Fragment>
      <Row
        mt='24'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Text ml='12' mb='08' font='display.16.bold'>
          Steps Log
        </Text>

        {isRetractable && (
          <Pressable
            mr='12'
            onPress={toggleExpanded}
            width='20'
            height='20'
            justifyContent='center'
            alignItems='center'
            style={{ transform: [{ rotate: !isExpanded ? '0deg' : '180deg' }] }}
          >
            <ChevronDownIcon fill={'shade07'} width={20} height={20} />
          </Pressable>
        )}
      </Row>
      <Card mt='08' px='00' py='00'>
        {isLoading ? (
          <Box pt='24'>
            <Loading mt='20' py='20' />
          </Box>
        ) : (
          <React.Fragment>
            {shouldRenderChart && (
              <BarChart
                height={100}
                barColor='walrusRed900'
                margin='16'
                valueList={stepsValues}
              />
            )}
            <Row m='16'>
              <Column width='33%'>
                <Text font='text.12.bold'>Date</Text>
              </Column>
              <Column width='33%'>
                <Text font='text.12.bold'>Time</Text>
              </Column>
              <Column width='33%'>
                <Text font='text.12.bold'>Steps Count</Text>
              </Column>
            </Row>

            {stepsLog?.results.length === 0 && (
              <Row mx='16' my='12'>
                <Text font='text.14.medium'>No data available yet.</Text>
              </Row>
            )}

            {stepsLog?.results.map((steps, index) => {
              if (index >= currentPageSize) {
                return null
              }
              const isRecent =
                moment().diff(moment(steps.timestamp), 'days') <= 7
              return (
                <Row key={index} mx='16' my='12'>
                  <Column width='33%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {moment(steps.timestamp, 'YYYY-MM-DDTHH:mm:ssZ').format(
                        'MM/DD/YYYY'
                      )}
                    </Text>
                  </Column>
                  <Column width='33%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {moment(steps.timestamp, 'YYYY-MM-DDTHH:mm:ssZ').format(
                        'hh:mm A'
                      )}
                    </Text>
                  </Column>
                  <Column width='33%' flexDirection='row' alignItems='center'>
                    {isRecent && (
                      <Box
                        backgroundColor='walrusBlue500'
                        width='12px'
                        height='12px'
                        mr='08'
                        borderRadius='xlarge'
                      />
                    )}
                    <Text font='text.14.medium'>
                      {steps.value.toLocaleString('en-US')}
                    </Text>
                  </Column>
                </Row>
              )
            })}
          </React.Fragment>
        )}
      </Card>
    </React.Fragment>
  )
}

export default StepsLog
